import * as React from "react";
import { midpointApiService, MidpointCase, Group } from "../services/midpointApi";
import { AdContext } from "../context/AdContext";
import Loader from "../components/Loader";
import styles from "../styles/approvals.module.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

const APPROVE_OUTCOME =
  "http://midpoint.evolveum.com/xml/ns/public/model/approval/outcome#approve";

// Extract sortable date from case — tries timestamps first, then parses from case name
function caseDate(c: MidpointCase): number {
  const ts = c.metadata?.createTimestamp || c.startTimestamp || c.openTimestamp;
  if (ts) return new Date(ts).getTime();
  // Fallback: extract date from case name e.g. "(started Jun 18, 2026, 7:39:46 AM)"
  const match = refName(c.name).match(/started\s+(.+?)\)/i);
  if (match) {
    const parsed = new Date(match[1]);
    if (!isNaN(parsed.getTime())) return parsed.getTime();
  }
  return 0;
}

function sortLatestFirst(cases: MidpointCase[]): MidpointCase[] {
  return [...cases].sort((a, b) => caseDate(b) - caseDate(a));
}

function refName(name?: string | { orig?: string; norm?: string }): string {
  if (!name) return "";
  if (typeof name === "object") return name.orig || "";
  return name;
}

function formatDate(ts?: string): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getFirstWorkItem(c: MidpointCase) {
  return c.workItem?.[0];
}

function caseOutcome(c: MidpointCase): "approved" | "rejected" | "open" | "cancelled" {
  if (c.state === "closed") {
    const outcome = getFirstWorkItem(c)?.output?.outcome;
    return outcome === APPROVE_OUTCOME ? "approved" : "rejected";
  }
  if (c.state === "cancelled") return "cancelled";
  return "open";
}

// ── Status Badge ──────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: ReturnType<typeof caseOutcome> }> = ({ status }) => {
  const labels: Record<string, string> = {
    open: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
  };
  return (
    <span className={`${styles.badge} ${styles[`badge_${status}`]}`}>
      {labels[status] ?? status}
    </span>
  );
};

// ── My Requests Tab ───────────────────────────────────────────────────────────

const MyRequests: React.FC<{ userOid: string }> = ({ userOid }) => {
  const [cases, setCases] = React.useState<MidpointCase[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await midpointApiService.getMyAccessRequests(userOid);
      setCases(sortLatestFirst(data));
    } catch (e: any) {
      setError(e.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [userOid]);

  React.useEffect(() => { load(); }, [load]);

  const handleCancel = async (c: MidpointCase) => {
    setCancelling(c.oid);
    try {
      await midpointApiService.cancelCase(c.oid);
      await load();
    } catch (e: any) {
      alert(e.message || "Failed to cancel request");
    } finally {
      setCancelling(null);
    }
  };

  if (loading) return <Loader />;
  if (error) return <div className={styles.errorMsg}>{error}</div>;
  if (cases.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📋</div>
        <div className={styles.emptyTitle}>No access requests</div>
        <div className={styles.emptySub}>You haven't requested access to any groups yet.</div>
      </div>
    );
  }

  return (
    <div className={styles.cardList}>
      {cases.map((c) => {
        const status = caseOutcome(c);
        const wi = getFirstWorkItem(c);
        return (
          <div key={c.oid} className={styles.card}>
            <div className={styles.cardTop}>
              <div className={styles.cardTitle}>
                {refName(c.targetRef?.targetName) || c.name || "Access Request"}
              </div>
              <StatusBadge status={status} />
            </div>
            <div className={styles.cardMeta}>
              <span>Requested: {formatDate(c.metadata?.createTimestamp || c.startTimestamp || c.openTimestamp)}</span>
              {wi?.deadline && <span>Deadline: {formatDate(wi.deadline)}</span>}
            </div>
            {status === "open" && (
              <div className={styles.cardActions}>
                <button
                  className={styles.btnDanger}
                  onClick={() => handleCancel(c)}
                  disabled={cancelling === c.oid}
                >
                  {cancelling === c.oid ? "Cancelling…" : "Cancel Request"}
                </button>
              </div>
            )}
            {status === "rejected" && wi?.output?.comment && (
              <div className={styles.rejectComment}>
                Reason: {wi.output.comment}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Pending Approvals Tab ─────────────────────────────────────────────────────

const PendingApprovals: React.FC<{ userOid: string }> = ({ userOid }) => {
  const [cases, setCases] = React.useState<MidpointCase[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [processing, setProcessing] = React.useState<string | null>(null);
  const [rejectComment, setRejectComment] = React.useState<Record<string, string>>({});
  const [showReject, setShowReject] = React.useState<Record<string, boolean>>({});
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await midpointApiService.getPendingApprovals(userOid);
      setCases(sortLatestFirst(data));
    } catch (e: any) {
      setError(e.message || "Failed to load pending approvals");
    } finally {
      setLoading(false);
    }
  }, [userOid]);

  React.useEffect(() => { load(); }, [load]);

  const handleApprove = async (c: MidpointCase) => {
    const wi = getFirstWorkItem(c);
    if (!wi) return;
    // Midpoint stores container ID as @id in JSON responses
    const wiId = (wi as any)['@id'] ?? wi.id;
    if (!wiId) { alert('Work item ID not found'); return; }
    setProcessing(c.oid);
    try {
      await midpointApiService.approveWorkItem(c.oid, wiId);
      setSuccessMsg(`Request "${c.name}" approved successfully.`);
      setTimeout(() => setSuccessMsg(null), 4000);
      await load();
    } catch (e: any) {
      alert(e.message || "Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (c: MidpointCase) => {
    const wi = getFirstWorkItem(c);
    if (!wi) return;
    const wiId = (wi as any)['@id'] ?? wi.id;
    if (!wiId) { alert('Work item ID not found'); return; }
    setProcessing(c.oid);
    try {
      await midpointApiService.rejectWorkItem(c.oid, wiId, rejectComment[c.oid] || "Rejected");
      setSuccessMsg(`Request "${c.name}" rejected.`);
      setTimeout(() => setSuccessMsg(null), 4000);
      await load();
    } catch (e: any) {
      alert(e.message || "Failed to reject");
    } finally {
      setProcessing(null);
      setShowReject((prev) => ({ ...prev, [c.oid]: false }));
    }
  };

  const successBanner = successMsg ? (
    <div style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontWeight: 500 }}>
      ✅ {successMsg}
    </div>
  ) : null;

  if (loading) return <Loader />;
  if (error) return <div className={styles.errorMsg}>{error}</div>;
  if (cases.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>✅</div>
        <div className={styles.emptyTitle}>No pending approvals</div>
        <div className={styles.emptySub}>All caught up — no requests waiting for your review.</div>
      </div>
    );
  }

  return (
    <div className={styles.cardList}>
      {successBanner}
      {cases.map((c) => {
        const wi = getFirstWorkItem(c);
        const busy = processing === c.oid;
        return (
          <div key={c.oid} className={styles.card}>
            <div className={styles.cardTop}>
              <div className={styles.cardTitle}>
                {refName(c.targetRef?.targetName) || c.name || "Access Request"}
              </div>
              <StatusBadge status="open" />
            </div>
            <div className={styles.cardMeta}>
              <span>Requested by: <strong>{refName(c.requestorRef?.targetName) || c.requestorRef?.oid || "Unknown"}</strong></span>
              <span>Date: {formatDate(c.metadata?.createTimestamp)}</span>
              {wi?.deadline && <span>Deadline: {formatDate(wi.deadline)}</span>}
            </div>

            {showReject[c.oid] ? (
              <div className={styles.rejectPanel}>
                <textarea
                  className={styles.rejectInput}
                  placeholder="Reason for rejection (optional)"
                  value={rejectComment[c.oid] || ""}
                  onChange={(e) =>
                    setRejectComment((prev) => ({ ...prev, [c.oid]: e.target.value }))
                  }
                  rows={2}
                />
                <div className={styles.cardActions}>
                  <button
                    className={styles.btnDanger}
                    onClick={() => handleReject(c)}
                    disabled={busy}
                  >
                    {busy ? "Rejecting…" : "Confirm Reject"}
                  </button>
                  <button
                    className={styles.btnSecondary}
                    onClick={() => setShowReject((prev) => ({ ...prev, [c.oid]: false }))}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.cardActions}>
                <button
                  className={styles.btnSuccess}
                  onClick={() => handleApprove(c)}
                  disabled={busy}
                >
                  {busy ? "Approving…" : "Approve"}
                </button>
                <button
                  className={styles.btnDanger}
                  onClick={() => setShowReject((prev) => ({ ...prev, [c.oid]: true }))}
                  disabled={busy}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Request Access Tab ────────────────────────────────────────────────────────

const PAGE_SIZE = 8;

const RequestAccess: React.FC<{ userOid: string; userEmail: string }> = ({ userOid, userEmail }) => {
  const [allRoles, setAllRoles] = React.useState<Group[]>([]);
  const [myRoleIds, setMyRoleIds] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [requesting, setRequesting] = React.useState<string | null>(null);
  const [requested, setRequested] = React.useState<Set<string>>(new Set());
  const [requestError, setRequestError] = React.useState<Record<string, string>>({});
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    Promise.allSettled([
      midpointApiService.getRequestableRoles(),
      midpointApiService.getUserAssignedRoles(userEmail),
    ]).then(([rolesResult, myRolesResult]) => {
      if (rolesResult.status === "fulfilled") {
        setAllRoles(rolesResult.value);
      } else {
        setError(rolesResult.reason?.message || "Failed to load roles");
      }
      if (myRolesResult.status === "fulfilled") {
        setMyRoleIds(new Set(myRolesResult.value.map((r) => r.id)));
      }
      setLoading(false);
    });
  }, [userEmail]);

  const handleRequest = async (role: Group) => {
    setRequesting(role.id);
    setRequestError((prev) => ({ ...prev, [role.id]: "" }));
    try {
      await midpointApiService.addMemberToGroup(userOid, role.id);
      setRequested((prev) => new Set(prev).add(role.id));
    } catch (e: any) {
      const msg = e.message || "";
      // Treat "insufficient permissions" as success — Midpoint queues it for approval
      // and returns a non-200 response which the client interprets as an error
      if (msg.toLowerCase().includes("insufficient") || msg.toLowerCase().includes("access denied") || msg.toLowerCase().includes("403")) {
        setRequested((prev) => new Set(prev).add(role.id));
      } else {
        setRequestError((prev) => ({ ...prev, [role.id]: msg || "Request failed" }));
      }
    } finally {
      setRequesting(null);
    }
  };

  const filtered = search
    ? allRoles.filter((r) => r.displayName.toLowerCase().includes(search.toLowerCase()))
    : allRoles;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when search changes
  React.useEffect(() => { setPage(1); }, [search]);

  if (loading) return <Loader />;
  if (error) return <div className={styles.errorMsg}>{error}</div>;

  return (
    <div className={styles.roleTable}>
      {/* Search + count */}
      <div className={styles.roleTableHeader}>
        <div className={styles.roleTableCount}>
          {filtered.length} role{filtered.length !== 1 ? "s" : ""}
        </div>
        <input
          className={styles.roleSearch}
          placeholder="Search roles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {paginated.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🔍</div>
          <div className={styles.emptyTitle}>No roles found</div>
          <div className={styles.emptySub}>Try a different search term.</div>
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Role</th>
              <th className={styles.th}>Description</th>
              <th className={styles.th} style={{ width: 160, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((role) => {
              const busy = requesting === role.id;
              const done = requested.has(role.id);
              const isMember = myRoleIds.has(role.id);
              const err = requestError[role.id];
              return (
                <tr key={role.id} className={styles.tr}>
                  <td className={styles.td} style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {role.displayName}
                  </td>
                  <td className={styles.td} style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                    {role.description || "—"}
                    {err && <div className={styles.errorMsg} style={{ marginTop: 4 }}>{err}</div>}
                  </td>
                  <td className={styles.td} style={{ textAlign: "right" }}>
                    {isMember ? (
                      <span className={`${styles.badge} ${styles.badge_approved}`}>Already a member</span>
                    ) : done ? (
                      <span className={`${styles.badge} ${styles.badge_open}`}>Requested</span>
                    ) : (
                      <button
                        className={styles.btnPrimary}
                        onClick={() => handleRequest(role)}
                        disabled={busy}
                      >
                        {busy ? "Requesting…" : "Request Access"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Prev
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Approvals Page ───────────────────────────────────────────────────────

const Approvals = () => {
  const { email } = React.useContext(AdContext);
  const [tab, setTab] = React.useState<"requests" | "approvals" | "request-access">("requests");
  const [userOid, setUserOid] = React.useState<string | null>(null);
  const [selfError, setSelfError] = React.useState<string | null>(null);

  React.useEffect(() => {
    midpointApiService
      .getSelf()
      .then((u) => setUserOid(u.oid))
      .catch((e) => setSelfError(e.message || "Could not identify current user"));
  }, []);

  if (selfError) {
    return <div className={styles.errorMsg}>{selfError}</div>;
  }

  if (!userOid) {
    return <Loader />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Approvals</h1>
      </div>

      <div className={styles.tabBar}>
        <button
          className={`${styles.tabBtn} ${tab === "request-access" ? styles.tabBtnActive : ""}`}
          onClick={() => setTab("request-access")}
        >
          Request Access
        </button>
        <button
          className={`${styles.tabBtn} ${tab === "requests" ? styles.tabBtnActive : ""}`}
          onClick={() => setTab("requests")}
        >
          My Requests
        </button>
        <button
          className={`${styles.tabBtn} ${tab === "approvals" ? styles.tabBtnActive : ""}`}
          onClick={() => setTab("approvals")}
        >
          Pending Approvals
        </button>
      </div>

      <div className={styles.tabContent}>
        {tab === "request-access" && <RequestAccess userOid={userOid} userEmail={email} />}
        {tab === "requests" && <MyRequests userOid={userOid} />}
        {tab === "approvals" && <PendingApprovals userOid={userOid} />}
      </div>
    </div>
  );
};

export default Approvals;
